/**
 * DirectProvisionPanel
 *
 * Capability-keyed setup panel rendered when a connector declares the
 * `direct-provision` capability. Used by connectors like
 * `retailstore` that are provisioned directly — no OAuth redirect and no
 * API-key form, but the user does supply config: a display name plus the
 * warehouses and price lists associated with the channel.
 *
 * Renders flush inside the modal `Stack` (no card chrome): the store-name
 * input, a config row that opens the warehouse/price-list picker, and —
 * once configured — the selected Depósitos / Listas de precios as chips
 * with a filled star on the default item (recovered from the legacy
 * `RetailStoreChannelRegistration`). The nested `RetailStoreConfigModal`,
 * form state, and the actual provisioning dispatch are owned by the caller
 * (`AddChannelModal`).
 */

import { Grid, Group, Stack, Text, TextInput, ThemeIcon, UnstyledButton } from '@mantine/core';
import { IconCheck, IconChevronRight, IconSettings, IconStarFilled } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { WarehousesReadmodelType, PriceListReadmodelType } from '@bett3r-dev/teselly-domain';
import { integrationsTestIds } from '@domains/integrations/testids';
import styles from './SetupPanel.module.css';

export type DirectProvisionPanelProps = {
  /** Connector id, used for the data-testids (e.g. `retailstore`). */
  connectorId: string;
  /** Human-readable connector label (passed by the caller; no longer rendered). */
  connectorLabel?: string;
  /** Controlled store-name value. */
  name: string;
  /** Store-name change handler. */
  onNameChange: ( value: string ) => void;
  /**
   * Whether the warehouse / price-list config has been completed.
   * Drives the green check vs. the neutral settings icon on the config row.
   */
  isConfigured: boolean;
  /** Selected warehouse ids. */
  warehouseIds: string[];
  /** Selected price-list ids. */
  priceListIds: string[];
  /** All available warehouses — used to resolve chip labels. */
  availableWarehouses: WarehousesReadmodelType[];
  /** All available price lists — used to resolve chip labels. */
  availablePriceLists: PriceListReadmodelType[];
  /** The default warehouse id — rendered with a filled star. */
  defaultChannelWarehouseId: string | null;
  /** The default price-list id — rendered with a filled star. */
  defaultPriceListId: string | null;
  /** Click handler — caller opens the nested RetailStoreConfigModal. */
  onOpenConfig: () => void;
  /** Disable the panel inputs (e.g. submit in flight). */
  disabled?: boolean;
  /**
   * Whether to show the warehouse / price-list config row + chips. The stock
   * association is a retailstore-only concern; other direct-provision connectors
   * (e.g. testchannel) render only the name field. Defaults to `true` so
   * existing retailstore callers are unaffected.
   */
  showInventoryConfig?: boolean;
};

const DirectProvisionPanel = ({
  connectorId,
  name,
  onNameChange,
  isConfigured,
  warehouseIds,
  priceListIds,
  availableWarehouses,
  availablePriceLists,
  defaultChannelWarehouseId,
  defaultPriceListId,
  onOpenConfig,
  disabled = false,
  showInventoryConfig = true
}: DirectProvisionPanelProps ) => {
  const { t } = useTranslation( 'integrations' );

  return (
    <Stack
      gap="sm"
      w="100%"
      data-testid={integrationsTestIds.setupDirectProvision( connectorId )}
    >
      <TextInput
        label={t( 'setup_panel.directProvision.name_label', 'Nombre de la tienda' )}
        size="sm"
        radius="xs"
        value={name}
        onChange={( e ) => onNameChange( e.currentTarget.value )}
        placeholder={t(
          'setup_panel.directProvision.name_placeholder',
          'Mi tienda'
        )}
        disabled={disabled}
        data-testid={integrationsTestIds.setupDirectProvisionNameInput( connectorId )}
      />

      {showInventoryConfig && (
      <>
      <UnstyledButton
        className={styles.configRow}
        onClick={onOpenConfig}
        disabled={disabled}
        data-testid={integrationsTestIds.setupDirectProvisionConfigRow( connectorId )}
      >
        <Group wrap="nowrap" gap="sm" className={styles.configRowMain}>
          <ThemeIcon
            variant="light"
            color={isConfigured ? 'green' : 'gray'}
            size="lg"
            radius="md"
            className={styles.configRowIcon}
          >
            {isConfigured ? (
              <IconCheck size={18} />
            ) : (
              <IconSettings size={18} />
            )}
          </ThemeIcon>
          <div className={styles.configRowText}>
            <Text className={styles.configRowTitle}>
              {t(
                'setup_panel.directProvision.config_title',
                'Inventario y listas de precios'
              )}
            </Text>
            <Text className={styles.configRowDescription}>
              {isConfigured
                ? t(
                    'setup_panel.directProvision.config_done',
                    'Configurado · {{count}} seleccionados',
                    { count: warehouseIds.length + priceListIds.length }
                  )
                : t(
                    'setup_panel.directProvision.config_pending',
                    'Selecciona los depósitos y listas predet. (opcional)'
                  )}
            </Text>
          </div>
        </Group>
        <IconChevronRight size={16} className={styles.configRowChevron} />
      </UnstyledButton>

      {warehouseIds.length > 0 && (
        <Grid align="center" justify="flex-start" gutter="md">
          <Grid.Col span={4} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Text
              size="10px"
              fw={600}
              tt="uppercase"
              ta="right"
              c="dimmed"
              style={{ letterSpacing: '1px' }}
            >
              {t( 'setup_panel.directProvision.warehouses_label', 'Depósitos' )}
            </Text>
          </Grid.Col>
          <Grid.Col span={8}>
            <Group gap={6} justify="flex-start">
              {warehouseIds.map(( id ) => {
                const warehouse = availableWarehouses.find(( w ) => w.id === id );
                if ( !warehouse ) return null;
                const isDefault = id === defaultChannelWarehouseId;
                return (
                  <div key={id} className={styles.configItem}>
                    {isDefault && (
                      <IconStarFilled size={12} className={styles.configItemStar} />
                    )}
                    <Text
                      size="xs"
                      lh={1}
                      style={{ marginTop: '2px' }}
                      fw={isDefault ? 600 : 400}
                    >
                      {warehouse.name}
                    </Text>
                  </div>
                );
              })}
            </Group>
          </Grid.Col>
        </Grid>
      )}

      {priceListIds.length > 0 && (
        <Grid align="center" justify="flex-start" gutter="md">
          <Grid.Col span={4} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Text
              size="10px"
              fw={600}
              tt="uppercase"
              ta="right"
              c="dimmed"
              style={{ letterSpacing: '1px', lineHeight: 1.2 }}
            >
              {t( 'setup_panel.directProvision.pricelists_label', 'Listas de precios' )}
            </Text>
          </Grid.Col>
          <Grid.Col span={8}>
            <Group gap={6} justify="flex-start">
              {priceListIds.map(( id ) => {
                const priceList = availablePriceLists.find(( p ) => p.id === id );
                if ( !priceList ) return null;
                const isDefault = id === defaultPriceListId;
                return (
                  <div key={id} className={styles.configItem}>
                    {isDefault && (
                      <IconStarFilled size={12} className={styles.configItemStar} />
                    )}
                    <Text
                      size="xs"
                      lh={1}
                      style={{ marginTop: '2px' }}
                      fw={isDefault ? 600 : 400}
                    >
                      {priceList.name}
                    </Text>
                  </div>
                );
              })}
            </Group>
          </Grid.Col>
        </Grid>
      )}
      </>
      )}
    </Stack>
  );
};

export default DirectProvisionPanel;
